import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import useFuzzyHighlight, { normalizeText } from '../hooks/useFuzzyHighlight';
import { getPageAnnouncementText } from '../utils/citationHelpers';

/**
 * Search the PDF document for the page that best matches highlightText.
 * Uses direct normalized substring match first (fast, for KB verbatim text),
 * then falls back to fuzzy match.
 * Returns the 1-indexed page number or null if no match found.
 */
async function findBestPage(pdfDoc, suggestedPage, highlightText) {
  if (!pdfDoc || !highlightText) return null;

  const totalPages = pdfDoc.numPages;
  const clamped = Math.max(1, Math.min(suggestedPage, totalPages));

  // Use shared normalizeText for consistency
  const normSearch = normalizeText(highlightText);

  // Search ±10 pages from suggested, then stop (don't scan all pages)
  const searchOrder = [clamped];
  for (let offset = 1; offset <= Math.min(10, totalPages); offset++) {
    if (clamped - offset >= 1) searchOrder.push(clamped - offset);
    if (clamped + offset <= totalPages) searchOrder.push(clamped + offset);
  }

  // First, try full text match on suggested page (fast path)
  try {
    const page = await pdfDoc.getPage(clamped);
    const textContent = await page.getTextContent();
    const pageText = normalizeText(textContent.items.map((item) => item.str).join(' '));
    if (pageText.toLowerCase().includes(normSearch.toLowerCase())) return clamped;
  } catch {
    // skip
  }

  // Generate multi-segment search strings from the normalized search text
  const len = normSearch.length;
  const segmentSize = 200;
  const segments = [];

  // Segment 1: beginning
  segments.push(normSearch.substring(0, Math.min(segmentSize, len)));
  // Segment 2: 1/3 position
  if (len > segmentSize) {
    const start1 = Math.floor(len / 3);
    segments.push(normSearch.substring(start1, start1 + Math.min(segmentSize, len - start1)));
  }
  // Segment 3: 2/3 position
  if (len > segmentSize * 2) {
    const start2 = Math.floor((2 * len) / 3);
    segments.push(normSearch.substring(start2, start2 + Math.min(segmentSize, len - start2)));
  }
  // Segment 4: end
  if (len > segmentSize) {
    segments.push(normSearch.substring(Math.max(0, len - segmentSize), len));
  }

  // Filter out very short segments (< 10 chars)
  const validSegments = segments.filter((s) => s.length >= 10);

  let bestPage = null;
  let bestCount = 0;

  for (const pageNum of searchOrder) {
    try {
      const page = await pdfDoc.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = normalizeText(textContent.items.map((item) => item.str).join(' '));
      const lowerPage = pageText.toLowerCase();

      let count = 0;
      for (const seg of validSegments) {
        if (lowerPage.includes(seg.toLowerCase())) {
          count++;
        }
      }

      if (count > bestCount || (count === bestCount && bestPage !== null && Math.abs(pageNum - suggestedPage) < Math.abs(bestPage - suggestedPage))) {
        bestCount = count;
        bestPage = pageNum;
      }
    } catch {
      // skip unreadable pages
    }
  }

  if (bestPage !== null && bestCount > 0) return bestPage;

  return null;
}

/**
 * PdfHighlightViewer renders a PDF page using react-pdf with text layer enabled,
 * and highlights cited text via fuzzy matching.
 *
 * When the highlight text isn't found on the suggested page, it searches
 * nearby pages (then all pages) using the pdfjs API and auto-navigates.
 */
function PdfHighlightViewer({
  documentPath,
  pageNumber,
  highlightText,
  citationText,
  documentName,
  onError,
}) {
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  const [textLayerReady, setTextLayerReady] = useState(false);
  const [textLayerVersion, setTextLayerVersion] = useState(0); // increments to force hook re-run
  const [containerWidth, setContainerWidth] = useState(null);
  const [resolvedPage, setResolvedPage] = useState(null);
  const [searching, setSearching] = useState(false);
  const [manualPage, setManualPage] = useState(null); // user-navigated page override

  const containerRef = useRef(null);
  const textLayerRef = useRef(null);
  const pdfDocRef = useRef(null);

  // Measure container width for scaling
  useEffect(() => {
    if (!containerRef.current) return;
    const measure = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // The page we actually render: manual override > resolved (from search) > clamped original
  const clampedPage = Math.max(1, Math.min(pageNumber, numPages || pageNumber));
  const activePage = manualPage || resolvedPage || clampedPage;

  // Reset text layer state when page or document changes
  useEffect(() => {
    setTextLayerReady(false);
    textLayerRef.current = null;
  }, [documentPath, activePage]);

  // Reset resolved page and manual page when citation changes
  useEffect(() => {
    setResolvedPage(null);
    setManualPage(null);
  }, [documentPath, pageNumber, highlightText]);

  // Fuzzy highlight integration — textLayerVersion forces re-run when text layer changes
  const { matchFound } = useFuzzyHighlight(textLayerRef, highlightText, textLayerReady, citationText, textLayerVersion);

  // When match fails on the current page, search other pages
  // Skip auto-search if user has manually navigated
  useEffect(() => {
    if (!textLayerReady || !highlightText || matchFound || resolvedPage || manualPage || !pdfDocRef.current) return;

    let cancelled = false;
    setSearching(true);

    findBestPage(pdfDocRef.current, pageNumber, highlightText).then((found) => {
      if (cancelled) return;
      setSearching(false);
      if (found && found !== activePage) {
        setResolvedPage(found);
      }
    });

    return () => { cancelled = true; };
  }, [textLayerReady, matchFound, highlightText, pageNumber, activePage, resolvedPage, manualPage]);

  const handleDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setError(null);

    // Grab the underlying pdfjs document for text search
    pdfjs.getDocument(documentPath).promise.then((doc) => {
      pdfDocRef.current = doc;
    }).catch(() => {});
  }, [documentPath]);

  const handleDocumentLoadError = useCallback(
    (err) => { setError(err); if (onError) onError(err); },
    [onError]
  );

  const handleRetry = useCallback(() => {
    setError(null);
    setNumPages(null);
    setTextLayerReady(false);
    setResolvedPage(null);
    textLayerRef.current = null;
    pdfDocRef.current = null;
  }, []);

  const handlePageRenderSuccess = useCallback(() => {
    if (!containerRef.current) return;
    setTimeout(() => {
      if (!containerRef.current) return;
      // Find the text layer for the CURRENT page specifically
      const pageEl = containerRef.current.querySelector('.react-pdf__Page');
      const textLayerDiv = pageEl ? pageEl.querySelector('.react-pdf__Page__textContent') : containerRef.current.querySelector('.react-pdf__Page__textContent');
      if (textLayerDiv) {
        textLayerRef.current = textLayerDiv;
        setTextLayerReady(true);
        setTextLayerVersion(v => v + 1);
      }
    }, 800);
  }, []);

  const showNoMatch = highlightText && textLayerReady && !matchFound && !searching && resolvedPage === activePage;

  if (error) {
    return (
      <div ref={containerRef} style={{ padding: '24px', textAlign: 'center' }} role="alert">
        <p style={{ marginBottom: '12px', color: '#d13212' }}>Failed to load "{documentName}".</p>
        <button onClick={handleRetry} style={{ padding: '8px 16px', cursor: 'pointer', border: '1px solid #545b64', borderRadius: '4px', backgroundColor: '#fff' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <div aria-live="polite" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
        {getPageAnnouncementText(documentName, activePage)}
      </div>

      {searching && (
        <div style={{ padding: '8px 12px', marginBottom: '8px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', fontSize: '13px', color: '#1565c0' }} role="status">
          Searching for text on nearby pages…
        </div>
      )}

      {showNoMatch && (
        <div style={{ padding: '8px 12px', marginBottom: '8px', backgroundColor: '#fef3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '13px', color: '#664d03' }} role="status">
          Exact text match not found on this page
        </div>
      )}



      {numPages && numPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '6px 0', marginBottom: '4px' }}>
          <button
            onClick={() => setManualPage(Math.max(1, activePage - 1))}
            disabled={activePage <= 1}
            aria-label="Previous page"
            style={{
              padding: '4px 10px', fontSize: '13px', cursor: activePage <= 1 ? 'default' : 'pointer',
              border: '1px solid #d5dbdb', borderRadius: '4px', backgroundColor: activePage <= 1 ? '#f2f3f3' : '#fff',
              color: activePage <= 1 ? '#aab7b8' : '#16191f',
            }}
          >
            ‹ Prev
          </button>
          <span style={{ fontSize: '12px', color: '#545b64' }}>
            Page {activePage} of {numPages}
          </span>
          <button
            onClick={() => setManualPage(Math.min(numPages, activePage + 1))}
            disabled={activePage >= numPages}
            aria-label="Next page"
            style={{
              padding: '4px 10px', fontSize: '13px', cursor: activePage >= numPages ? 'default' : 'pointer',
              border: '1px solid #d5dbdb', borderRadius: '4px', backgroundColor: activePage >= numPages ? '#f2f3f3' : '#fff',
              color: activePage >= numPages ? '#aab7b8' : '#16191f',
            }}
          >
            Next ›
          </button>
        </div>
      )}

      <Document file={documentPath} onLoadSuccess={handleDocumentLoadSuccess} onLoadError={handleDocumentLoadError}>
        <Page pageNumber={activePage} renderTextLayer={true} renderAnnotationLayer={false} width={containerWidth || undefined} onRenderTextLayerSuccess={handlePageRenderSuccess} />
      </Document>
    </div>
  );
}

export default PdfHighlightViewer;
